/**
 * LibraryAdapter.ts — the async port every library source implements.
 *
 * Why this exists separately from `StorageBackend`:
 *  - `StorageBackend` is **synchronous** (localStorage is sync). It powers UI
 *    prefs and was the historical home for saved scenarios. A remote/HTTP
 *    library is inherently async — it cannot implement a sync interface.
 *  - The library also needs richer metadata per entry (uuid, slug, node count,
 *    timestamps) that does not belong on the wire-format `ScenarioBundle`
 *    (Pydantic `extra="forbid"`, byte-equality tested).
 *
 * So `LibraryAdapter` is the async, metadata-rich port the UI consumes. The
 * adapter knows nothing about React — `useLibrary` is the React binding.
 *
 * Implementations:
 *  - `LocalLibraryAdapter`    — wraps `StorageBackend` (localStorage).
 *                               Default in pure-browser mode.
 *  - `RemoteLibraryAdapter`   — HTTP against `/api/library/*`. Used in
 *                               localhost-server and SaaS modes.
 *
 * The shape of `LibraryEntry` mirrors `backend/library/contract.py`'s
 * `LibraryEntry` model so the same JSON travels over the wire unchanged.
 */
import type { ScenarioBundle } from '../engine/types';

/** One library item — metadata only, no graph payload. The graph is fetched
 *  on demand via `get(id)` so list/search stay cheap. */
export interface LibraryEntry {
  /** Stable, immutable uuid. Survives rename. The storage key. */
  id: string;
  /** Machine id derived from the label, unique within the library. Used as the
   *  default filename on export. The user never types this directly. */
  slug: string;
  /** The human title — what the user sees and renames. */
  label: string;
  description?: string;
  /** User-assigned tags for filtering the library. Free-form strings; the UI
   *  normalizes to lowercase. Empty array when none set. */
  tags: string[];
  /** Number of actors in the bundle — the one bit of graph-derived metadata
   *  we precompute so list rows read usefully without loading every bundle. */
  nodeCount: number;
  /** Epoch ms of last save. */
  updatedAt: number;
  /** Epoch ms of first creation (best-effort; absent for migrated legacy). */
  createdAt?: number;
  /** Where this entry lives. UI uses it to badge rows; the adapter sets it. */
  source: 'local' | 'remote';
}

export interface LibraryAdapter {
  /** All entries, unfiltered. Implementations sort newest-first. */
  list(): Promise<LibraryEntry[]>;
  /** Entries whose label/description/slug/tags match the query (case-
   *  insensitive). Empty query returns the same set as `list()`. */
  search(query: string): Promise<LibraryEntry[]>;
  /** Fetch the full bundle for an entry, or null if it's gone. */
  get(id: string): Promise<ScenarioBundle | null>;
  /** Save (insert or replace by id). When `slug` is provided it's used
   *  verbatim; otherwise one is derived from the bundle label. Throws on slug
   *  collision with a different entry. Returns the resulting entry. */
  save(bundle: ScenarioBundle, opts?: { slug?: string; tags?: string[] }): Promise<LibraryEntry>;
  /** Rename the entry's TITLE (label). The slug is re-derived from the new
   *  label (with a uniqueness suffix) so the file stays addressable. Throws on
   *  slug collision. */
  rename(id: string, label: string): Promise<LibraryEntry>;
  /** Replace an entry's tags wholesale. Pass [] to clear. */
  setTags(id: string, tags: string[]): Promise<LibraryEntry>;
  /** Delete by id. No-op + resolves for missing ids. */
  delete(id: string): Promise<void>;
  /** Save a foreign bundle (uploaded file) into the library, minting a new
   *  uuid. Distinct from `save` so callers can flag the "first import" case
   *  (e.g. set createdAt) and the UI can show an upload-success toast. */
  importBundle(bundle: ScenarioBundle, opts?: { slug?: string; tags?: string[] }): Promise<LibraryEntry>;
  /** Create a new blank project from just a NAME — the backend mints the id
   *  (slugified label, suffixed on collision) and scaffolds the full project
   *  (manifest with every surface enabled, empty Main graph, tables spec,
   *  docker-compose.yml). Optional: adapters without a server-side scaffold
   *  fall back to `importBundle`. */
  createBlank?(label: string, opts?: { description?: string; tags?: string[] }): Promise<LibraryEntry>;
}

/** Library-aware error so the UI can branch on collision vs generic failure. */
export class LibraryError extends Error {
  readonly code: 'collision' | 'not-found' | 'network' | 'invalid';
  constructor(code: LibraryError['code'], message: string) {
    super(message);
    this.name = 'LibraryError';
    this.code = code;
  }
}
